import React, { FC, useRef, useState } from "react";
import { Col, Table, Row, Button, Input, Space, Tooltip } from "antd";
import CustomTooltip from "@pages/tool-tip";
import { SearchOutlined } from "@ant-design/icons";
import { map, cloneDeep, get, filter } from "lodash";
import Highlighter from "react-highlight-words";
import { centricClientCheck, fixedDecimal, formatDisplayDate } from "@utils/helpers";
import { TableLocale } from "@components/noDataFound";

const PlaceOfService: FC<any> = (props) => {
  const data = props?.data;
  const isCentric = centricClientCheck();
  const [tableData, setTableData] = useState(data);
  const [columnSearchInfo, setColumnSearchInfo] = useState({});
  const inputRef = useRef<any>(null);

  let tableColumns = filter(
    [
      {
        title: "DATE",
        dataIndex: "date",
        width: "15%",
        ellipsis: {
          showTitle: false,
        },
        render: (date: any) => {
          const formattedDate = formatDisplayDate(date, "reminderDateActualFormat", "");
          return <CustomTooltip toolTipvalue={formattedDate} />;
        },
        sorter: (a: any, b: any) => {
          return a.date?.localeCompare(b.date);
        },
      },
      {
        title: "POS CODE",
        dataIndex: "posCode",
        width: "15%",
        ellipsis: {
          showTitle: false,
        },
        render: (posCode: any) => <CustomTooltip toolTipvalue={posCode} />,
        sorter: (a: any, b: any) => {
          return a.posCode - b.posCode;
        },
      },
      {
        title: "CLAIM SERVICE TYPE",
        dataIndex: "claimServiceType",
        width: "23%",
        ellipsis: {
          showTitle: false,
        },
        render: (claimServiceType: any) => {
          return <CustomTooltip toolTipvalue={claimServiceType} />;
        },
        sorter: (a: any, b: any) => {
          return a.claimServiceType?.localeCompare(b.claimServiceType);
        },
      },
      {
        title: "COST",
        dataIndex: "cost",
        width: "15%",
        ellipsis: {
          showTitle: false,
        },
        className: !isCentric && "textAlignRight",
        render: (cost: any, data: any) => {
          const children = (
            <div>
              <span style={{ fontWeight: "bold", marginRight: "8px" }}>$ </span>
              {`${fixedDecimal(cost, 2)} `}
            </div>
          );
          return <CustomTooltip toolTipvalue={children} />;
        },
        sorter: (a: any, b: any) => {
          return a.cost - b.cost;
        },
      },
      {
        title: "PLACE OF SERVICE NAME",
        dataIndex: "placeOfServiceDesc",
        ellipsis: {
          showTitle: false,
        },
        render: (date: any) => <CustomTooltip toolTipvalue={date} />,
        sorter: (a: any, b: any) => {
          return a.placeOfServiceDesc?.localeCompare(b.placeOfServiceDesc);
        },
      },
    ],
    (_col) => {
      return !(!isCentric && ["claimServiceType"].includes(_col.dataIndex));
    }
  );

  React.useEffect(() => {
    setTableData(data);
  }, [data]);

  const highlighterDiv = (text: any, searchWord: any) => {
    return (
      <Tooltip placement="topLeft" title={text ? text.toString() : ""}>
        <Highlighter highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }} searchWords={[searchWord]} autoEscape textToHighlight={text ? text.toString() : ""} />
      </Tooltip>
    );
  };

  const applyColumnSearch = (columnName: any, text: any) => {
    text = text ? text : get(columnSearchInfo, [columnName], "");
    if (text !== "") {
      let tempMedicationsData = data.filter((data: any) => {
        if (data[columnName]) {
          return data[columnName].toString().toLowerCase().includes(text.toLowerCase());
        } else {
          return false;
        }
      });
      setTableData(tempMedicationsData);
    } else {
      setTableData(data);
    }
  };

  const columns: Array<{}> = map(cloneDeep(tableColumns), (dataInfo: any) => {
    return {
      ...dataInfo,

      filterDropdown: ({ confirm }: any) => (
        <div style={{ padding: "10px" }}>
          <Input
            style={{ marginBottom: 8, display: "block" }}
            value={get(columnSearchInfo, dataInfo.dataIndex, "")}
            ref={inputRef}
            onChange={(e) => {
              setColumnSearchInfo({
                ...columnSearchInfo,
                [dataInfo.dataIndex]: e.target.value,
              });
            }}
            onPressEnter={(e: any): any => {
              applyColumnSearch(dataInfo.dataIndex, "");
              confirm({ closeDropdown: true });
              e.stopPropagation();
            }}
          />
          <Space>
            <Button
              type="primary"
              icon={<SearchOutlined />}
              size="small"
              style={{ width: 90 }}
              onClick={(e) => {
                setColumnSearchInfo({
                  [dataInfo.dataIndex]: get(columnSearchInfo, dataInfo.dataIndex, ""),
                });
                applyColumnSearch(dataInfo.dataIndex, "");
                confirm({ closeDropdown: true });
              }}
            >
              Search
            </Button>
            <Button
              onClick={() => {
                setColumnSearchInfo({ [dataInfo.dataIndex]: "" });
                applyColumnSearch("", "");
                confirm({ closeDropdown: true });
              }}
              size="small"
              style={{ width: 90 }}
            >
              Reset
            </Button>
          </Space>
        </div>
      ),
      filterIcon:
        dataInfo.dataIndex === "action"
          ? null
          : () => (
              <SearchOutlined
                style={{
                  color: get(columnSearchInfo, dataInfo.dataIndex, "") !== "" ? "#EF6D2A" : undefined,
                }}
              />
            ),
      render:
        dataInfo.dataIndex === "date"
          ? dataInfo.render
          : dataInfo.dataIndex === "cost"
          ? (text: any, record: any) => highlighterDiv("$ " + fixedDecimal(text, 2), get(columnSearchInfo, dataInfo.dataIndex, ""))
          : (text: any, record: any) => highlighterDiv(text, get(columnSearchInfo, dataInfo.dataIndex, "")),
      onFilter: (value: any, record: any) => (record[dataInfo.dataIndex] ? record[dataInfo.dataIndex].toString().toLowerCase().includes(value.toLowerCase()) : ""),
      onFilterDropdownOpenChange: (visible: any) => {
        if (visible) {
          setTimeout(() => inputRef.current?.select(), 100);
        }
      },
    };
  });

  return (
    <div>
      <Row>
        <Col span={24}>
          <Table
            bordered={true}
            pagination={{ defaultPageSize: 5, showSizeChanger: true, pageSizeOptions: ["5", "10", "15", "20", "25", "30"] }}
            columns={columns}
            dataSource={tableData}
            className="table-rows"
            locale={TableLocale}
          />
        </Col>
      </Row>
    </div>
  );
};

export default PlaceOfService;
